import React, { useEffect, useState } from "react";
import {
  Box,
  Typography,
  Paper,
  Stack,
  Button,
  Avatar,
  Chip,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  IconButton,
  InputAdornment,
  Divider,
  CircularProgress,
} from "@mui/material";
import LogoutIcon from "@mui/icons-material/Logout";
import LockResetOutlinedIcon from "@mui/icons-material/LockResetOutlined";
import VisibilityOutlinedIcon from "@mui/icons-material/VisibilityOutlined";
import VisibilityOffOutlinedIcon from "@mui/icons-material/VisibilityOffOutlined";
import LockOutlinedIcon from "@mui/icons-material/LockOutlined";
import KeyboardArrowRightIcon from "@mui/icons-material/KeyboardArrowRight";
import { useFormik } from "formik";
import * as Yup from "yup";
import { useDispatch, useSelector } from "react-redux";
import { useNavigate } from "react-router-dom";
import toast from "react-hot-toast";
import {
  logoutThunk,
  changePassword,
  updateProfile,
} from "../features/auth/authSlice";
import { SUN_GRADIENT } from "../theme";

// ── Password dialog ─────────────────────────────────────────────────
const passwordSchema = Yup.object({
  currentPassword: Yup.string().required("Required"),
  newPassword: Yup.string().min(4, "Min 4 characters").required("Required"),
  confirmPassword: Yup.string()
    .oneOf([Yup.ref("newPassword")], "Passwords do not match")
    .required("Required"),
});

function ChangePasswordDialog({ open, onClose }) {
  const dispatch = useDispatch();
  const [show, setShow] = useState({ cur: false, next: false, confirm: false });
  const formik = useFormik({
    initialValues: { currentPassword: "", newPassword: "", confirmPassword: "" },
    validationSchema: passwordSchema,
    onSubmit: async (values, { resetForm, setSubmitting }) => {
      const res = await dispatch(
        changePassword({
          currentPassword: values.currentPassword,
          newPassword: values.newPassword,
        })
      );
      if (changePassword.fulfilled.match(res)) {
        toast.success("Password updated");
        resetForm();
        onClose();
      } else {
        toast.error(res.payload || "Failed to change password");
      }
      setSubmitting(false);
    },
  });
  const fld = (name, label, key) => (
    <TextField
      fullWidth
      size="small"
      label={label}
      type={show[key] ? "text" : "password"}
      autoComplete="off"
      value={formik.values[name]}
      onChange={formik.handleChange(name)}
      onBlur={formik.handleBlur(name)}
      error={formik.touched[name] && Boolean(formik.errors[name])}
      helperText={formik.touched[name] && formik.errors[name]}
      InputProps={{
        endAdornment: (
          <InputAdornment position="end">
            <IconButton
              size="small"
              onClick={() => setShow((s) => ({ ...s, [key]: !s[key] }))}
            >
              {show[key] ? (
                <VisibilityOffOutlinedIcon fontSize="small" />
              ) : (
                <VisibilityOutlinedIcon fontSize="small" />
              )}
            </IconButton>
          </InputAdornment>
        ),
      }}
    />
  );
  return (
    <Dialog
      open={open}
      onClose={onClose}
      maxWidth="xs"
      fullWidth
      PaperProps={{ sx: { borderRadius: 3 } }}
    >
      <DialogTitle sx={{ pb: 1, fontWeight: 700 }}>Change password</DialogTitle>
      <form onSubmit={formik.handleSubmit}>
        <DialogContent sx={{ pt: 1 }}>
          <Stack spacing={1.5}>
            {fld("currentPassword", "Current password", "cur")}
            {fld("newPassword", "New password", "next")}
            {fld("confirmPassword", "Confirm new password", "confirm")}
          </Stack>
        </DialogContent>
        <DialogActions sx={{ px: 3, pb: 2 }}>
          <Button onClick={onClose} variant="text">Cancel</Button>
          <Button type="submit" variant="contained" disabled={formik.isSubmitting}>
            {formik.isSubmitting ? "Saving…" : "Update"}
          </Button>
        </DialogActions>
      </form>
    </Dialog>
  );
}

// ── Profile page ────────────────────────────────────────────────────
export default function ProfilePage() {
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const { user } = useSelector((s) => s.auth);

  const [name, setName] = useState(user?.name || "");
  const [phone, setPhone] = useState(user?.phone || "");
  const [savingName, setSavingName] = useState(false);
  const [savingPhone, setSavingPhone] = useState(false);
  const [pwOpen, setPwOpen] = useState(false);

  // Keep local fields in sync if the user object refreshes from the server.
  useEffect(() => {
    setName(user?.name || "");
    setPhone(user?.phone || "");
  }, [user?.name, user?.phone]);

  const initials = (user?.name || "U")
    .split(" ")
    .map((n) => n[0])
    .join("")
    .slice(0, 2)
    .toUpperCase();

  const saveName = async () => {
    const trimmed = name.trim();
    if (!trimmed || trimmed === user?.name) return;
    setSavingName(true);
    const res = await dispatch(updateProfile({ name: trimmed }));
    setSavingName(false);
    if (updateProfile.fulfilled.match(res)) toast.success("Name updated");
    else {
      toast.error(res.payload || "Save failed");
      setName(user?.name || "");
    }
  };

  const savePhone = async () => {
    if ((phone || "") === (user?.phone || "")) return;
    setSavingPhone(true);
    const res = await dispatch(updateProfile({ phone: phone.trim() }));
    setSavingPhone(false);
    if (updateProfile.fulfilled.match(res)) toast.success("Mobile updated");
    else {
      toast.error(res.payload || "Save failed");
      setPhone(user?.phone || "");
    }
  };

  const onLogout = async () => {
    await dispatch(logoutThunk());
    toast.success("Signed out");
    navigate("/login");
  };

  return (
    <Box
      sx={{
        px: { xs: 1, md: 3 },
        py: { xs: 2.5, md: 3 },
        width: { xs: "94%", sm: "92%", md: "auto" },
        maxWidth: 640,
        mx: "auto",
      }}
    >
      <Paper variant="outlined" sx={{ p: { xs: 2, md: 3 }, borderRadius: 3 }}>
        {/* Avatar + username */}
        <Stack alignItems="center" spacing={1.25} sx={{ mb: 2.5 }}>
          <Avatar
            sx={{
              background: SUN_GRADIENT,
              color: "#fff",
              width: 84,
              height: 84,
              fontSize: "1.625rem",
              fontWeight: 700,
              boxShadow: "0 4px 14px rgba(13,46,117,0.16)",
            }}
          >
            {initials}
          </Avatar>
          <Chip
            icon={<LockOutlinedIcon sx={{ fontSize: 14 }} />}
            label={user?.email || "—"}
            size="small"
            sx={{
              fontWeight: 600,
              fontFamily: "monospace",
              backgroundColor: "rgba(13,46,117,0.06)",
            }}
          />
          <Typography variant="caption" color="text.secondary">
            Username can't be changed
          </Typography>
        </Stack>

        <Divider sx={{ mb: 2 }} />

        {/* Editable fields — labels above, not inside the input */}
        <Stack spacing={2.5}>
          <Box>
            <Typography
              variant="caption"
              sx={{ display: "block", mb: 1, fontWeight: 600, color: "text.secondary", textTransform: "uppercase", letterSpacing: 0.4, fontSize: "0.6875rem" }}
            >
              Name
            </Typography>
            <TextField
              fullWidth
              size="small"
              placeholder="Your name"
              value={name}
              onChange={(e) => setName(e.target.value)}
              onBlur={saveName}
              onKeyDown={(e) => { if (e.key === "Enter") e.currentTarget.blur(); }}
              InputProps={{
                endAdornment: savingName ? (
                  <InputAdornment position="end">
                    <CircularProgress size={14} />
                  </InputAdornment>
                ) : null,
              }}
            />
          </Box>
          <Box>
            <Typography
              variant="caption"
              sx={{ display: "block", mb: 1, fontWeight: 600, color: "text.secondary", textTransform: "uppercase", letterSpacing: 0.4, fontSize: "0.6875rem" }}
            >
              Mobile Number
            </Typography>
            <TextField
              fullWidth
              size="small"
              placeholder="9999999999"
              value={phone}
              onChange={(e) => setPhone(e.target.value)}
              onBlur={savePhone}
              onKeyDown={(e) => { if (e.key === "Enter") e.currentTarget.blur(); }}
              inputProps={{ inputMode: "tel" }}
              InputProps={{
                endAdornment: savingPhone ? (
                  <InputAdornment position="end">
                    <CircularProgress size={14} />
                  </InputAdornment>
                ) : null,
              }}
            />
          </Box>
        </Stack>

        <Divider sx={{ my: 2 }} />

        {/* Change password */}
        <Stack
          direction="row"
          alignItems="center"
          onClick={() => setPwOpen(true)}
          sx={{
            cursor: "pointer",
            px: 1,
            py: 1.25,
            borderRadius: 2,
            transition: "background-color 0.15s",
            "&:hover": { backgroundColor: "rgba(13,46,117,0.04)" },
          }}
        >
          <Box
            sx={{
              width: 36,
              height: 36,
              borderRadius: 1.5,
              backgroundColor: "rgba(13,46,117,0.08)",
              color: "primary.main",
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              mr: 1.5,
            }}
          >
            <LockResetOutlinedIcon fontSize="small" />
          </Box>
          <Box sx={{ flex: 1 }}>
            <Typography variant="body2" sx={{ fontWeight: 700 }}>
              Change password
            </Typography>
            <Typography variant="caption" color="text.secondary">
              Rotate your sign-in password
            </Typography>
          </Box>
          <KeyboardArrowRightIcon sx={{ color: "text.disabled" }} />
        </Stack>
      </Paper>

      {/* Sign out */}
      <Button
        fullWidth
        size="large"
        variant="contained"
        color="error"
        startIcon={<LogoutIcon />}
        onClick={onLogout}
        sx={{
          py: 1.25,
          mt: 2,
          background: "linear-gradient(135deg, #bb0000, #ee5b29)",
          fontWeight: 700,
          letterSpacing: 0.3,
          boxShadow: "0 4px 14px rgba(187,0,0,0.25)",
          "&:hover": {
            background: "linear-gradient(135deg, #a30000, #d44e1e)",
            filter: "brightness(0.95)",
          },
        }}
      >
        Sign Out
      </Button>

      <ChangePasswordDialog open={pwOpen} onClose={() => setPwOpen(false)} />
    </Box>
  );
}
